/**
 * Typed API surface for opncue.
 *
 * Each function maps 1:1 to a NestJS endpoint. When `USE_MOCK` is true the
 * call is served from the in-memory mock backend instead.
 */

import type {
    AuthSession,
    ChatMessage,
    ChatInbox,
    ChatSessionPage,
    ChatSession,
    AdminClaim,
    CreateSmallGroupInput,
    EndConversationOutcome,
    GrowthSummary,
    ReportInput,
    OwnerSpot,
    PublicSpot,
    Reflection,
    ReflectionExport,
    ReflectionExportOptions,
    ReflectionHistoryQuery,
    ReflectionInput,
    ReflectionPage,
    ReflectionQuota,
    SmallGroup,
    TrendPoint,
    SmallGroupInboxSections,
    GroupBrowseFilters,
    SmallGroupInvite,
    SocialGoal,
    SmallGroupMessage,
    Cue,
    CueCounts,
    GuestProfile,
    InvitePreview,
    InviteRedemption,
    InviteView,
    MintedInvite,
    BlockedUser,
    NearbyResult,
    PhotoExchangeState,
    PhotoUpload,
    ByoIntakeResult,
    PracticeCredits,
    PracticeMode,
    PracticeVoice,
    PracticePack,
    PracticePackView,
    PracticeScenario,
    PracticeSendResult,
    PracticeSession,
    PracticeSummaryResponse,
    PracticeMessage,
    PracticeSessionView,
    ProgressDashboard,
    AgeVerificationPolicy,
    BillingAddress,
    RevealResult,
    SubscriptionPreview,
    SubscriptionStart,
    SubscriptionSummary,
    SupportInput,
    SupportSubmitResult,
    User,
    UserAvatarConfig,
    Venue,
    MoodCatalog,
    MoodCatalogItem,
    MoodContextCatalogItem,
    MoodJarEntry,
    MoodJarSummary,
    MonthlyVibeCard,
    MonthlyVibeCardStatusResponse,
    ConversationDeckBootstrapResponse,
    ConversationDeckRefillResponse,
    ConversationDeckServerCategory,
} from "@/types";
import { exportQuery } from "@/lib/export-request";
import type { Coords } from "@/lib/location";
import {
  authedImageSource,
  fetchAuthedBlob,
  request,
  uploadImage,
} from "./client";
import { API_BASE_URL, USE_MOCK } from "./config";
import { mockApi } from "./mock";

export type WebPushSubscriptionPayload = {
  endpoint: string;
  expirationTime?: number | null;
  keys: {
    p256dh: string;
    auth: string;
  };
};

type MoodCatalogResponse =
  | MoodCatalogItem[]
  | {
      moods?: MoodCatalogItem[];
      contexts?: MoodContextCatalogItem[];
      primaryMoods?: MoodCatalogItem[];
    };

type MoodJarEntryResponse = MoodJarEntry & {
  primaryMood?: string | null;
};

const FALLBACK_MOOD_CONTEXTS: MoodContextCatalogItem[] = [
  { id: "work", emoji: "💼" },
  { id: "people", emoji: "👥" },
  { id: "home", emoji: "🏠" },
  { id: "sleep", emoji: "🛌" },
  { id: "health", emoji: "🫶" },
  { id: "plans", emoji: "📅" },
  { id: "weather", emoji: "☁️" },
  { id: "money", emoji: "🪙" },
  { id: "one_of_those_days", emoji: "〰️" },
  { id: "other", emoji: "✎" },
];

const OTHER_MOOD: MoodCatalogItem = {
  id: "other",
  emoji: "✎",
  valence: 0,
  energy: "medium",
  social: "guarded",
  gentle: false,
};

function normalizeMoodCatalog(value: MoodCatalogResponse): MoodCatalog {
  if (Array.isArray(value)) {
    return { moods: withOtherMood(value), contexts: FALLBACK_MOOD_CONTEXTS };
  }
  const moods = Array.isArray(value.moods)
    ? value.moods
    : Array.isArray(value.primaryMoods)
      ? value.primaryMoods
      : [];
  const contexts = Array.isArray(value.contexts) ? value.contexts : FALLBACK_MOOD_CONTEXTS;
  return { moods: withOtherMood(moods), contexts };
}

function withOtherMood(moods: MoodCatalogItem[]): MoodCatalogItem[] {
  return moods.some((m) => m.id === "other") ? moods : [...moods, OTHER_MOOD];
}

function normalizeMoodJarEntry(entry: MoodJarEntryResponse): MoodJarEntry {
  return {
    id: entry.id,
    moodId: entry.moodId ?? entry.primaryMood ?? "",
    moodOther: entry.moodOther ?? null,
    intensity: entry.intensity,
    contextId: entry.contextId ?? null,
    contextOther: entry.contextOther ?? null,
    createdAt: entry.createdAt,
  };
}

export const authApi = {
  /** POST /auth/start — email a one-time login code. */
  start: (email: string, locale?: string | null) =>
    USE_MOCK
      ? mockApi.authStart(email)
      : request<{ ok: boolean; channel: "email"; email: string }>(
          "/auth/start",
          {
            method: "POST",
            auth: false,
            body: { email, locale },
          },
        ),

  /** POST /auth/verify — exchange OTP for tokens. */
  verify: (email: string, code: string, locale?: string | null) =>
    USE_MOCK
      ? mockApi.authVerify(email)
      : request<AuthSession>("/auth/verify", {
          method: "POST",
          auth: false,
          body: { email, code, locale },
        }),

  /** POST /auth/refresh — rotate an expired access token using the refresh token. */
  refresh: (refreshToken: string) =>
    USE_MOCK
      ? mockApi.authRefresh(refreshToken)
      : request<AuthSession>("/auth/refresh", {
          method: "POST",
          auth: false,
          body: { refreshToken },
        }),

  /** POST /auth/google — exchange a Google ID token for app tokens. */
  google: (idToken: string, locale?: string | null) =>
    USE_MOCK
      ? mockApi.authSocial("google")
      : request<AuthSession>("/auth/google", {
          method: "POST",
          auth: false,
          body: { idToken, locale },
        }),

  /** POST /auth/apple — exchange an Apple identity token for app tokens. */
  apple: (
    identityToken: string,
    fullName?: string,
    nonce?: string,
    locale?: string | null,
  ) =>
    USE_MOCK
      ? mockApi.authSocial("apple", fullName)
      : request<AuthSession>("/auth/apple", {
          method: "POST",
          auth: false,
          body: { identityToken, fullName, nonce, locale },
        }),

  /**
   * POST /auth/revoke — native sign-out. Revokes the refresh session AND
   * releases its push registrations, addressed by the refresh token alone (no
   * access token). Resolves only on 204 (both cleanups done); a 503 rejects and
   * the caller keeps the local session so the user stays signed in to retry.
   */
  revoke: (refreshToken: string) =>
    USE_MOCK
      ? Promise.resolve()
      : request<void>("/auth/revoke", {
          method: "POST",
          auth: false,
          body: { refreshToken },
        }),
};

/**
 * The browser transport for the same auth system (auth-web.controller.ts).
 *
 * Same flows, one difference: the refresh token lives in an HttpOnly cookie
 * the server sets — these responses never contain it, and this client never
 * sees it. `webAuth: true` is what sends the cookie and the guard's required
 * header; nothing outside this block may use it. Mock mode reuses the native
 * mocks: the mock session's refreshToken is simply ignored on web.
 */
export type WebAuthSession = Omit<AuthSession, "refreshToken">;

export const webAuthApi = {
  verify: (email: string, code: string, locale?: string | null) =>
    USE_MOCK
      ? mockApi.authVerify(email)
      : request<WebAuthSession>("/auth/web/verify", {
          method: "POST",
          auth: false,
          webAuth: true,
          body: { email, code, locale },
        }),

  google: (idToken: string, locale?: string | null) =>
    USE_MOCK
      ? mockApi.authSocial("google")
      : request<WebAuthSession>("/auth/web/google", {
          method: "POST",
          auth: false,
          webAuth: true,
          body: { idToken, locale },
        }),

  // Same parameter order as authApi.apple, so the store's fork cannot
  // transpose nonce and locale.
  apple: (
    identityToken: string,
    fullName?: string,
    nonce?: string,
    locale?: string | null,
  ) =>
    USE_MOCK
      ? mockApi.authSocial("apple", fullName)
      : request<WebAuthSession>("/auth/web/apple", {
          method: "POST",
          auth: false,
          webAuth: true,
          body: { identityToken, fullName, locale, nonce },
        }),

  /** Rebuild the session from the cookie alone — the page-reload path. */
  refresh: () =>
    USE_MOCK
      ? mockApi.authRefresh("mock-refresh")
      : request<WebAuthSession>("/auth/web/refresh", {
          method: "POST",
          auth: false,
          webAuth: true,
        }),

  /** Revoke via the cookie — works even when the access token has expired. */
  logout: () =>
    USE_MOCK
      ? Promise.resolve()
      : request<void>("/auth/web/logout", {
          method: "POST",
          // The cookie is the ONLY credential this route acts on; a bearer
          // token riding along would contradict that confinement and rot into
          // a dependency (review, 2026-09-02).
          auth: false,
          webAuth: true,
        }),
};

export const usersApi = {
  /** GET /users/me */
  me: () => (USE_MOCK ? mockApi.getMe() : request<User>("/users/me")),

  /** PATCH /users/me — set profile, vibe, mode. */
  update: (patch: Partial<User>) =>
    USE_MOCK
      ? mockApi.updateMe(patch)
      : request<User>("/users/me", { method: "PATCH", body: patch }),

  /** PATCH /users/me/avatar — save a customized illustrated avatar config. */
  updateAvatar: (avatarConfig: UserAvatarConfig) =>
    USE_MOCK
      ? mockApi.updateAvatar(avatarConfig)
      : request<User>("/users/me/avatar", {
          method: "PATCH",
          body: { avatarConfig },
        }),

  /**
   * DEV ONLY: PATCH /users/me/dev-plan — force this account's plan so Plus
   * features can be tested without a real subscription. The server route is
   * disabled in production.
   */
  setDevPlan: (plan: "free" | "plus") =>
    USE_MOCK
      ? mockApi.setDevPlan(plan)
      : request<User>("/users/me/dev-plan", {
          method: "PATCH",
          body: { plan },
        }),

  /**
   * POST /users/me/deactivate — go quiet without losing anything.
   *
   * The reversible half of leaving: no cues, messages or Small Groups, and no
   * profile anyone else can open, while Practice, goals and reflections carry
   * on. Returns the new state so the button can flip without a refetch.
   */
  deactivateAccount: () =>
    USE_MOCK
      ? mockApi.deactivateAccount()
      : request<{ deactivatedAt: string | null }>("/users/me/deactivate", {
          method: "POST",
        }),

  /** POST /users/me/reactivate — undo the above. */
  reactivateAccount: () =>
    USE_MOCK
      ? mockApi.reactivateAccount()
      : request<{ deactivatedAt: string | null }>("/users/me/reactivate", {
          method: "POST",
        }),

  /** DELETE /users/me — permanently delete the account and all its data. */
  deleteAccount: () =>
    USE_MOCK
      ? mockApi.deleteAccount()
      : request<{ ok: boolean }>("/users/me", { method: "DELETE" }),

  /** GET /users/:id/guest-profile — public/revealed details for another person. */
  guestProfile: (userId: string) =>
    USE_MOCK
      ? mockApi.getGuestProfile(userId)
      : request<GuestProfile>(
          `/users/${encodeURIComponent(userId)}/guest-profile`,
        ),

  /** POST /users/me/push-token — register the Expo push token for this device. */
  setPushToken: (token: string, platform?: "ios" | "android") =>
    USE_MOCK
      ? Promise.resolve({ ok: true })
      : request<{ ok: boolean }>("/users/me/push-token", {
          method: "POST",
          body: { token, platform },
        }),

  /** GET /users/me/web-push-public-key — VAPID public key for browser push. */
  webPushPublicKey: () =>
    USE_MOCK
      ? Promise.resolve<{ publicKey: string | null }>({ publicKey: null })
      : request<{ publicKey: string | null }>("/users/me/web-push-public-key", {
          auth: false,
        }),

  /** POST /users/me/web-push-subscription — register this browser. */
  setWebPushSubscription: (
    subscription: WebPushSubscriptionPayload | null,
  ) =>
    USE_MOCK
      ? Promise.resolve({ ok: true })
      : request<{ ok: boolean }>("/users/me/web-push-subscription", {
          method: "POST",
          body: { subscription },
        }),
};

/**
 * Member referrals — invite friends, both get Plus once the new member sticks.
 * Cash/affiliate partners are a separate (Phase 2) program, not this surface.
 */
export const referralsApi = {
  /** GET /referrals/me — the caller's code, link, and outgoing progress. */
  me: () =>
    USE_MOCK
      ? mockApi.getInviteView()
      : request<InviteView>("/referrals/me"),

  /** POST /referrals/redeem — apply an invite code during onboarding. */
  redeem: (code: string) =>
    USE_MOCK
      ? mockApi.redeemReferral(code)
      : request<{ ok: boolean }>("/referrals/redeem", {
          method: "POST",
          body: { code },
        }),

  /** POST /referrals/app-open — retention heartbeat on app foreground. */
  appOpen: () =>
    USE_MOCK
      ? Promise.resolve()
      : request<void>("/referrals/app-open", { method: "POST" }).catch(
          () => undefined,
        ),
};

/**
 * Invite links that carry their sender's intent.
 *
 * Sits beside `referralsApi` rather than inside it, mirroring the server. A
 * referral code is stable, reused for life, and only attributes genuinely new
 * signups; an envelope is single-use, expires in seven days, and works for a
 * friend who already has the app — because "come and talk to me" is a thing you
 * say to one person on one day, and it has to reach the person you actually
 * meant rather than only a stranger who has never installed anything.
 *
 * Redeeming one attempts the referral attribution too, so this does not replace
 * that surface, it wraps it.
 *
 * No `USE_MOCK` branch, unlike every surface above. That is deliberate and it
 * is a stated gap rather than an oversight: `useMock` is `false` in dev, stg
 * and prod alike (every `public.yaml` under `environments/`), so it is a
 * per-developer
 * override, and a fake single-use link with a fake expiry that mints a fake
 * Cue would exercise none of the behaviour that matters here. Under a local
 * mock override these calls hit the network and fail loudly, which is the
 * honest outcome — a stub that quietly succeeded would make the mint cap, the
 * single-use claim and the expiry all look like they worked.
 */
export const invitesApi = {
  /**
   * POST /invites — mint a link.
   *
   * Call this on SHARE, not on screen open. Every mint spends a slot against
   * the sender's daily cap, and a screen that minted on mount would burn the
   * whole allowance for somebody who opened it and shared nothing.
   */
  mint: (cueIntent: boolean) =>
    request<MintedInvite>("/invites", {
      method: "POST",
      body: { cueIntent },
    }),

  /** GET /invites/:code — who sent this, and whether it can still be claimed. */
  preview: (code: string) =>
    request<InvitePreview>(`/invites/${encodeURIComponent(code)}`),

  /** POST /invites/:code/redeem — claim it. Single-use, server-enforced. */
  redeem: (code: string) =>
    request<InviteRedemption>(
      `/invites/${encodeURIComponent(code)}/redeem`,
      { method: "POST" },
    ),
};

/**
 * Mood Jar — the month-long mood collection. Adding a mood NEVER triggers AI;
 * it just records a controlled feeling and intensity.
 */
export const moodJarApi = {
  /** GET /me/mood-jar/catalog — the controlled mood vocabulary. */
  catalog: () =>
    USE_MOCK
      ? mockApi.moodJarCatalog()
      : request<MoodCatalogResponse>("/me/mood-jar/catalog").then(normalizeMoodCatalog),

  /** GET /me/mood-jar/summary — this month's collecting state. */
  summary: () =>
    USE_MOCK
      ? mockApi.moodJarSummary()
      : request<MoodJarSummary>("/me/mood-jar/summary"),

  /** GET /me/mood-jar — the user's recent jar entries. */
  recent: () =>
    USE_MOCK
      ? mockApi.moodJarRecent()
      : request<MoodJarEntryResponse[]>("/me/mood-jar").then((rows) =>
          rows.map(normalizeMoodJarEntry),
        ),

  /** POST /me/mood-jar — drop a mood into the jar. */
  add: (input: {
    moodId: string;
    moodOther?: string | null;
    intensity?: number;
    contextId?: string | null;
    contextOther?: string | null;
  }) =>
    USE_MOCK
      ? mockApi.moodJarAdd(input)
      : request<MoodJarEntryResponse>("/me/mood-jar", {
          method: "POST",
          body: input,
        }).then(normalizeMoodJarEntry),

  /** DELETE /me/mood-jar/:id — remove one owned entry. */
  remove: (id: string) =>
    USE_MOCK
      ? mockApi.moodJarRemove(id)
      : request<void>(`/me/mood-jar/${encodeURIComponent(id)}`, {
          method: "DELETE",
        }),

  /**
   * DEV ONLY: backfill the jar with backdated entries (default: the previous
   * completed month) so the Monthly Vibe Card can be revealed without waiting
   * for month-end. Backend rejects this in production.
   */
  devSeed: (input: { periodKey?: string; count?: number; activeDays?: number } = {}) =>
    USE_MOCK
      ? mockApi.moodJarDevSeed(input)
      : request<MoodJarSummary>("/me/mood-jar/dev/seed", {
          method: "POST",
          body: input,
        }),
};

/**
 * Monthly Vibe Card — opncue's end-of-month interpretation of the Mood Jar.
 * The verdict is generated ONLY on `reveal`; every GET is read-only and never
 * incurs AI cost.
 */
export const monthlyVibeCardApi = {
  /** GET /me/monthly-vibe-cards/status — collecting month + latest eligibility. */
  status: () =>
    USE_MOCK
      ? mockApi.monthlyVibeCardStatus()
      : request<MonthlyVibeCardStatusResponse>("/me/monthly-vibe-cards/status"),

  /** GET /me/monthly-vibe-cards/:periodKey — a month's card or eligibility. */
  getForPeriod: (periodKey: string) =>
    USE_MOCK
      ? mockApi.monthlyVibeCardGet(periodKey)
      : request<MonthlyVibeCard>(
          `/me/monthly-vibe-cards/${encodeURIComponent(periodKey)}`,
        ),

  /** POST /me/monthly-vibe-cards/:periodKey/reveal — generate one text verdict. */
  reveal: (periodKey: string) =>
    USE_MOCK
      ? mockApi.monthlyVibeCardReveal(periodKey)
      : request<MonthlyVibeCard>(
          `/me/monthly-vibe-cards/${encodeURIComponent(periodKey)}/reveal`,
          { method: "POST" },
        ),
};

export const metricsApi = {
  /** GET /metrics/progress — the four-part Progress story for the window. */
  progress: (days: 7 | 30 = 7, since?: string | null) =>
    USE_MOCK
      ? mockApi.getProgressDashboard(days, since)
      : request<ProgressDashboard>(
          `/metrics/progress?days=${days}${since ? `&since=${encodeURIComponent(since)}` : ""}`,
        ),

  /** GET /metrics/summary — the user's growth totals for the window (days). */
  summary: (days = 7, since?: string | null) =>
    USE_MOCK
      ? mockApi.getGrowthSummary(days, since)
      : request<GrowthSummary>(
          `/metrics/summary?days=${days}${since ? `&since=${encodeURIComponent(since)}` : ""}`,
        ),

  /**
   * GET /metrics/trends — trend buckets for the progress chart. `unit` sets the
   * bucket size (one bar per day or per week) so the chart matches the Week /
   * Month range; `buckets` is how many bars to return.
   */
  trends: (unit: "day" | "week" = "week", buckets = 8, since?: string | null) =>
    USE_MOCK
      ? mockApi.getTrends(unit, buckets, since)
      : request<TrendPoint[]>(
          `/metrics/trends?unit=${unit}&buckets=${buckets}${since ? `&since=${encodeURIComponent(since)}` : ""}`,
        ),
};

export const reflectionsApi = {
  /** POST /reflections — log a reflection. */
  create: (input: ReflectionInput) =>
    USE_MOCK
      ? mockApi.createReflection(input)
      : request<Reflection>("/reflections", { method: "POST", body: input }),

  /** GET /reflections — recent reflection history. */
  recent: (limit = 10) =>
    USE_MOCK
      ? mockApi.getReflections(limit)
      : request<Reflection[]>(`/reflections?limit=${limit}`),

  /**
   * GET /reflections/history — the full journal, filtered and paged.
   *
   * Pass `cursor` from the previous page's `nextCursor` to load more; a null
   * `nextCursor` means there is nothing older left to fetch.
   */
  history: (query: ReflectionHistoryQuery = {}) =>
    USE_MOCK
      ? mockApi.getReflectionHistory(query)
      : request<ReflectionPage>(
          `/reflections/history${reflectionHistoryParams(query)}`,
        ),

  /** GET /reflections/quota — today's remaining reflections + plan limits. */
  quota: () =>
    USE_MOCK
      ? mockApi.getReflectionQuota()
      : request<ReflectionQuota>("/reflections/quota"),

  /**
   * GET /reflections/export — data export (Plus only).
   *
   * The range and section choices go to the server rather than being applied
   * after the fact: picking "last 30 days, journal only" used to still pull the
   * whole history, a year of totals and a year of weekly buckets.
   */
  export: (options: ReflectionExportOptions = {}) =>
    USE_MOCK
      ? mockApi.exportReflections(options)
      : request<ReflectionExport>(
          `/reflections/export${exportQuery(options)}`,
        ),

  /** DELETE /reflections/:id — the only thing that removes a reflection. */
  remove: (id: string) =>
    USE_MOCK
      ? mockApi.deleteReflection(id)
      : request<{ deleted: true }>(`/reflections/${encodeURIComponent(id)}`, {
          method: "DELETE",
        }),
};

/** Serialise only the filters that are actually set, so the URL stays readable. */
function reflectionHistoryParams(query: ReflectionHistoryQuery): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === "") continue;
    params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

export const goalsApi = {
  /** GET /goals — the user's small personal social goals. */
  list: () =>
    USE_MOCK ? mockApi.getGoals() : request<SocialGoal[]>("/goals"),

  /** POST /goals — set a new goal. Only the text is required. */
  create: (
    text: string,
    dueDate: string | null = null,
    notes: string[] = [],
  ) =>
    USE_MOCK
      ? mockApi.createGoal(text, dueDate, notes)
      : request<SocialGoal>("/goals", {
          method: "POST",
          body: { text, dueDate, notes },
        }),

  /** PATCH /goals/:id — check off / reopen. */
  setDone: (id: string, done: boolean) =>
    USE_MOCK
      ? mockApi.setGoalDone(id, done)
      : request<SocialGoal>(`/goals/${encodeURIComponent(id)}`, {
          method: "PATCH",
          body: { done },
        }),

  /**
   * PATCH /goals/:id — update editable goal details. Keys left off are not
   * touched; pass `dueDate: null` to clear a due date.
   */
  update: (
    id: string,
    input: { text?: string; dueDate?: string | null; notes?: string[] },
  ) =>
    USE_MOCK
      ? mockApi.updateGoal(id, input)
      : request<SocialGoal>(`/goals/${encodeURIComponent(id)}`, {
          method: "PATCH",
          body: input,
        }),

  /** POST /goals/:id/archive — keep a goal for review without occupying a slot. */
  archive: (id: string) =>
    USE_MOCK
      ? mockApi.archiveGoal(id)
      : request<SocialGoal>(`/goals/${encodeURIComponent(id)}/archive`, {
          method: "POST",
        }),

  /** POST /goals/:id/restore — bring an archived goal back out of the archive. */
  restore: (id: string) =>
    USE_MOCK
      ? mockApi.restoreGoal(id)
      : request<SocialGoal>(`/goals/${encodeURIComponent(id)}/restore`, {
          method: "POST",
        }),

  /** DELETE /goals/:id */
  remove: (id: string) =>
    USE_MOCK
      ? mockApi.removeGoal(id)
      : request<{ ok: boolean }>(`/goals/${encodeURIComponent(id)}`, {
          method: "DELETE",
        }),
};

export const billingApi = {
  /**
   * GET /billing/currency — the currency this user pays in (usd|gbp|eur),
   * resolved server-side from where the request actually comes from. The
   * client never chooses a currency; it only renders this one.
   */
  currency: () =>
    USE_MOCK
      ? Promise.resolve<{ currency: string; country: string | null }>({
          currency: "gbp",
          country: "GB",
        })
      : request<{ currency: string; country: string | null }>(
          "/billing/currency",
        ),

  /**
   * GET /billing/trial — whether this user gets the free Plus trial and its
   * length. Entirely policy-decided server-side; `trigger` names the surface
   * asking ("free_limit_reached", "plus_paywall_opened", …) so the policy
   * can limit which moments make the pitch.
   */
  trial: (trigger?: string) =>
    USE_MOCK
      ? Promise.resolve<{ eligible: boolean; days: number }>({
          eligible: true,
          days: 7,
        })
      : request<{ eligible: boolean; days: number }>(
          trigger
            ? `/billing/trial?trigger=${encodeURIComponent(trigger)}`
            : "/billing/trial",
        ),

  /** POST /billing/checkout — start a web Stripe Checkout for opncue Plus. */
  checkout: (plan?: "plus" | "plus_monthly" | "plus_annual") =>
    USE_MOCK
      ? Promise.resolve<{ url: string }>({ url: "" })
      : request<{ url: string }>("/billing/checkout", {
          method: "POST",
          body: plan ? { plan } : undefined,
        }),

  /**
   * POST /billing/subscription/preview — price a plan for the in-app checkout
   * summary. Read-only; nothing is created and nothing is charged.
   */
  previewSubscription: (
    plan?: "plus" | "plus_monthly" | "plus_annual",
    address?: BillingAddress,
  ) =>
    USE_MOCK
      ? Promise.resolve<SubscriptionPreview>({
          lines: [{ label: "opncue Plus", amount: 999 }],
          subtotal: 999,
          tax: 0,
          taxInclusive: false,
          total: 999,
          currency: "gbp",
          interval: "month",
          nextBillingDate: new Date(
            Date.now() + 30 * 24 * 3600 * 1000,
          ).toISOString(),
        })
      : request<SubscriptionPreview>("/billing/subscription/preview", {
          method: "POST",
          body: {
            ...(plan ? { plan } : {}),
            ...(address ? { address } : {}),
          },
        }),

  /**
   * POST /billing/subscription — start an in-app subscription and get the client
   * secret Stripe Elements needs to confirm the first payment.
   */
  createSubscription: (
    plan?: "plus" | "plus_monthly" | "plus_annual",
    address?: BillingAddress,
  ) =>
    USE_MOCK
      ? Promise.resolve<SubscriptionStart>({
          subscriptionId: "sub_mock",
          clientSecret: "",
          status: "incomplete",
        })
      : request<SubscriptionStart>("/billing/subscription", {
          method: "POST",
          body: {
            ...(plan ? { plan } : {}),
            ...(address ? { address } : {}),
          },
        }),

  /**
   * POST /billing/subscription/sync — reconcile after the browser confirms
   * payment. Server-authoritative: only the owner can sync their own
   * subscription, and it only ever upgrades.
   */
  syncSubscription: (subscriptionId: string) =>
    USE_MOCK
      ? Promise.resolve<SubscriptionSummary>({ active: true, plan: "plus" })
      : request<SubscriptionSummary>("/billing/subscription/sync", {
          method: "POST",
          body: { subscriptionId },
        }),

  /** POST /billing/portal — open the Stripe billing portal to manage/cancel. */
  portal: () =>
    USE_MOCK
      ? Promise.resolve<{ url: string }>({ url: "" })
      : request<{ url: string }>("/billing/portal", { method: "POST" }),

  /**
   * POST /billing/confirm — verify a Checkout Session on return and fulfil it if
   * paid. Server-authoritative: only the owner can confirm, so it's safe against
   * crafted return URLs. Idempotent.
   */
  confirm: (sessionId: string) =>
    USE_MOCK
      ? Promise.resolve<{ status: "paid" | "unpaid"; kind: "plus" | "pack" }>({
          status: "paid",
          kind: "pack",
        })
      : request<{ status: "paid" | "unpaid"; kind: "plus" | "pack" }>(
          "/billing/confirm",
          { method: "POST", body: { sessionId } },
        ),

  /** GET /billing/subscription — live subscription summary for the manage screen. */
  subscription: () =>
    USE_MOCK
      ? Promise.resolve<SubscriptionSummary>({
          active: true,
          plan: "plus",
          status: "active",
          amount: 999,
          currency: "gbp",
          interval: "month",
          currentPeriodEnd: new Date(
            Date.now() + 30 * 24 * 3600 * 1000,
          ).toISOString(),
          cancelAtPeriodEnd: false,
          paymentMethod: {
            brand: "visa",
            last4: "4242",
            expMonth: 1,
            expYear: 2029,
          },
        })
      : request<SubscriptionSummary>("/billing/subscription", {
          method: "GET",
        }),

  /** POST /billing/cancel — cancel at period end (keeps access until then). */
  cancel: () =>
    USE_MOCK
      ? Promise.resolve<SubscriptionSummary>({
          active: true,
          plan: "plus",
          cancelAtPeriodEnd: true,
        })
      : request<SubscriptionSummary>("/billing/cancel", { method: "POST" }),

  /** POST /billing/resume — undo a pending cancellation. */
  resume: () =>
    USE_MOCK
      ? Promise.resolve<SubscriptionSummary>({
          active: true,
          plan: "plus",
          cancelAtPeriodEnd: false,
        })
      : request<SubscriptionSummary>("/billing/resume", { method: "POST" }),

  /** POST /billing/setup-intent — start a PCI-safe card capture (web Elements). */
  setupIntent: () =>
    USE_MOCK
      ? Promise.resolve<{ clientSecret: string }>({ clientSecret: "" })
      : request<{ clientSecret: string }>("/billing/setup-intent", {
          method: "POST",
        }),

  /** POST /billing/payment-method — set a confirmed card as the default. */
  setPaymentMethod: (paymentMethodId: string) =>
    USE_MOCK
      ? Promise.resolve<SubscriptionSummary>({ active: true, plan: "plus" })
      : request<SubscriptionSummary>("/billing/payment-method", {
          method: "POST",
          body: { paymentMethodId },
        }),

  /** PATCH /billing/customer — update billing name/address (no card data). */
  updateBillingInfo: (info: {
    name?: string;
    address?: Record<string, string>;
  }) =>
    USE_MOCK
      ? Promise.resolve<{ ok: true }>({ ok: true })
      : request<{ ok: true }>("/billing/customer", {
          method: "PATCH",
          body: info,
        }),
};

const NOT_REQUIRED_POLICY: AgeVerificationPolicy = {
  status: "self_declared_only",
  minimumAgeRequired: 18,
  ageBand: "18_plus",
  strongerVerificationRequired: false,
  canUseApp: true,
  blockedFeatures: [],
};

/* The user-initiated verification API is GONE, deliberately (owner's rule,
 * 2026-09-03): users do not request age verification — Opncue requests it of
 * specific users, and the targeted flow (components/verification/verify-age)
 * rides `ageAssuranceApi` below when it is mounted. The server enforces the
 * same rule: POST /me/age-assurance/verification-session refuses unless an
 * active requirement or the config rule demands a check. */

export const ageAssuranceApi = {
  /** GET /me/age-assurance — resolved safe policy for the current user. */
  get: () =>
    USE_MOCK
      ? Promise.resolve<AgeVerificationPolicy>(NOT_REQUIRED_POLICY)
      : request<AgeVerificationPolicy>("/me/age-assurance", { method: "GET" }),

  /** POST /me/age-assurance/verification-session — start a provider session. */
  startVerificationSession: () =>
    USE_MOCK
      ? Promise.resolve<{
          provider: string;
          providerVerificationId: string;
          status: "pending";
          redirectUrl?: string;
        }>({
          provider: "mock",
          providerVerificationId: "mock",
          status: "pending",
        })
      : request<{
          provider: string;
          providerVerificationId: string;
          status: "pending";
          redirectUrl?: string;
        }>("/me/age-assurance/verification-session", {
          method: "POST",
          // No body: the server derives the audit reason from the
          // requirement that authorized the session, and the geo context
          // from the edge. Nothing the client could send here is trusted.
        }),
};

export const supportApi = {
  /** POST /support — file a Help & Support ticket (Report / Feedback / Contact). */
  submit: (input: SupportInput) =>
    USE_MOCK
      ? mockApi.submitSupport(input)
      : request<SupportSubmitResult>("/support", {
          method: "POST",
          body: input,
        }),
};

export const spotApi = {
  /** POST /business/claim — claim a venue (from a search candidate). */
  claim: (candidate: VenueCandidate) =>
    USE_MOCK
      ? mockApi.claimSpot(candidate)
      : request<OwnerSpot>("/business/claim", {
          method: "POST",
          body: {
            placeId: candidate.placeId,
            provider: candidate.provider,
            name: candidate.name,
            category: candidate.category,
            address: candidate.address,
            lat: candidate.lat,
            lng: candidate.lng,
          },
        }),

  /** GET /business/spots — spots this account owns. */
  mySpots: () =>
    USE_MOCK ? mockApi.getMySpots() : request<OwnerSpot[]>("/business/spots"),

  /** GET /business/spots/:id — the spot dashboard. */
  dashboard: (id: string) =>
    USE_MOCK
      ? mockApi.getSpotDashboard(id)
      : request<OwnerSpot>(`/business/spots/${encodeURIComponent(id)}`),

  /** PATCH /business/spots/:id — edit welcome note + meetup spots. */
  update: (
    id: string,
    patch: { welcomeNote?: string | null; meetupSpots?: string[] },
  ) =>
    USE_MOCK
      ? mockApi.updateSpot(id, patch)
      : request<OwnerSpot>(`/business/spots/${encodeURIComponent(id)}`, {
          method: "PATCH",
          body: patch,
        }),

  /** GET /spot/:slug — public spot page (no auth). */
  publicSpot: (slug: string) =>
    USE_MOCK
      ? mockApi.getPublicSpot(slug)
      : request<PublicSpot>(`/spot/${encodeURIComponent(slug)}`, {
          auth: false,
        }),

  admin: {
    /** GET /business/admin/claims — pending claims. */
    pendingClaims: () =>
      USE_MOCK
        ? mockApi.getPendingClaims()
        : request<AdminClaim[]>("/business/admin/claims"),

    approve: (id: string) =>
      USE_MOCK
        ? mockApi.setClaimStatus(id, "approved")
        : request<OwnerSpot>(
            `/business/admin/claims/${encodeURIComponent(id)}/approve`,
            { method: "POST" },
          ),

    reject: (id: string) =>
      USE_MOCK
        ? mockApi.setClaimStatus(id, "rejected")
        : request<OwnerSpot>(
            `/business/admin/claims/${encodeURIComponent(id)}/reject`,
            { method: "POST" },
          ),
  },
};

// Re-exported rather than redeclared. This was a second, narrower definition
// of the same shape, and a duplicate here is exactly how a field that must
// reach the server — the fix's accuracy — gets silently dropped in transit.
export type { Coords } from "@/lib/location";

/**
 * "venue" — a real named place; selectable and joinable.
 * "area"  — street/locality context shown when nothing named matched (e.g. a
 *           home address). Display only; the API rejects joining one.
 * Optional so older responses without the field still parse as venues.
 */
export type VenueCandidateKind = "venue" | "area";

export type VenueCandidate = {
  placeId: string;
  provider?: "geoapify" | "google_places";
  kind?: VenueCandidateKind;
  name: string;
  category?: string;
  address?: string;
  lat: number;
  lng: number;
};

type JoinVenueCandidate = VenueCandidate & {
  gpsLat?: number;
  gpsLng?: number;
  /** Metres of uncertainty in the GPS reading; the join is refused without it. */
  gpsAccuracy?: number;
  /** How old the fix already was, so the server dates it by capture, not receipt. */
  gpsAgeMs?: number;
};

export const venuesApi = {
  /** GET /venues/current — save GPS and resolve a local current space. */
  current: (coords?: Coords) => {
    if (USE_MOCK) return mockApi.getCurrentVenue();
    // `acc` travels with every fix. The server stores it and refuses to apply
    // any radius rule to a coordinate vaguer than the radius in question, so
    // dropping it here would make the user invisible rather than lenient.
    const params = new URLSearchParams();
    if (coords) {
      params.set("lat", String(coords.lat));
      params.set("lng", String(coords.lng));
      if (coords.accuracyMeters !== null) {
        params.set("acc", String(coords.accuracyMeters));
      }
      // How old the fix already was. Sent on every check-in because this is the
      // ONLY write path that records when a coordinate was taken, and without
      // it the server dates a five-minute-old cached fix as if measured now.
      params.set("age", String(coords.ageMs));
    }
    const qs = params.size > 0 ? `?${params.toString()}` : "";
    return request<Venue | null>(`/venues/current${qs}`);
  },

  /** GET /venues/search — explicit venue search; may call external provider. */
  search: (q: string, coords?: Coords) => {
    if (USE_MOCK) return mockApi.searchVenues(q, coords);
    const params = new URLSearchParams({ q });
    if (coords) {
      params.set("lat", String(coords.lat));
      params.set("lng", String(coords.lng));
    }
    return request<VenueCandidate[]>(`/venues/search?${params.toString()}`);
  },

  /** POST /venues/join — join a venue chosen from search. */
  join: (candidate: VenueCandidate, gps?: Coords) => {
    const body: JoinVenueCandidate = {
      placeId: candidate.placeId,
      provider: candidate.provider,
      name: candidate.name,
      category: candidate.category,
      address: candidate.address,
      lat: candidate.lat,
      lng: candidate.lng,
      gpsLat: gps?.lat,
      gpsLng: gps?.lng,
      gpsAccuracy: gps?.accuracyMeters ?? undefined,
      gpsAgeMs: gps?.ageMs,
    };

    return USE_MOCK
      ? mockApi.joinVenue(body)
      : request<Venue>("/venues/join", { method: "POST", body });
  },

  /** GET /venues/nearby — cached local places near GPS; no external call. */
  nearby: (coords?: Coords) => {
    if (USE_MOCK) return mockApi.searchVenues("", coords);
    if (!coords) return Promise.resolve<VenueCandidate[]>([]);
    const qs = `?lat=${coords.lat}&lng=${coords.lng}`;
    return request<VenueCandidate[]>(`/venues/nearby${qs}`);
  },
};

export const presenceApi = {
  /**
   * GET /presence/available — visible people inside the mutual range.
   *
   * Empty when the server cannot place the caller, and empty is the whole
   * answer: there is no venue or platform-wide fallback behind it any more. See
   * `docs/architecture/location-and-visibility.md`.
   *
   * `nearby()` sat beside this and called `GET /presence/nearby`, which nothing
   * in the app ever invoked. Both the endpoint and the wrapper are gone.
   *
   * Returns `{ people, total, nextCursor }`. `people` is one bounded page;
   * the headcount is derived from the rendered list and the cursor since the
   * step-10 count removal, and `total` is a deprecated shim equal to
   * `people.length` that nothing may read anew. Pass `cursor` to continue
   * from a previous page's `nextCursor`. See `NearbyResult`.
   */
  available: (cursor?: string) => {
    if (USE_MOCK) return mockApi.getNearby();
    const qs = cursor ? `?cursor=${encodeURIComponent(cursor)}` : "";
    return request<NearbyResult>(`/presence/available${qs}`);
  },

  /**
   * GET /presence/same-space — the same list, narrowed to your current venue.
   *
   * Same wire shape as `available`, so the list renders identically either
   * way, and the same paging contract. Sends NO venue: the server reads the
   * caller's own current space from presence, because a venue in the request
   * would be a list of strangers in a place the client merely named.
   *
   * Empty is a real answer here and means one of three things the client does
   * not need to tell apart — no current space the server will stand behind, a
   * fallback grid cell rather than a named place, or nobody visible in it.
   *
   * Cursors do not cross between this and `available`: the token carries the
   * scope, so one issued against the radius list is refused and paging
   * restarts. That is the correct recovery, not an error.
   */
  sameSpace: (cursor?: string) => {
    if (USE_MOCK) return mockApi.getNearby();
    const qs = cursor ? `?cursor=${encodeURIComponent(cursor)}` : "";
    return request<NearbyResult>(`/presence/same-space${qs}`);
  },

  /**
   * POST /presence/same-space — opt in to being seen by the other people who
   * joined this space.
   *
   * The venue id is the one the user just JOINED, and sending it is the point:
   * the server's own guess at where somebody is standing is wrong often enough
   * that naming the place has to be the user's job. The server does not take
   * it on trust — it re-proves the claim against the position on the presence
   * row, with a fresher-and-stricter version of the gate `POST /venues/join`
   * already applied — so this call can be refused and the caller must handle
   * that rather than assume it landed.
   */
  joinSameSpace: (venueId: string) => {
    if (USE_MOCK) return Promise.resolve({ ok: true });
    return request<{ ok: boolean }>("/presence/same-space", {
      method: "POST",
      body: { venueId },
    });
  },

  /**
   * DELETE /presence/same-space — leave.
   *
   * The only thing that clears participation. Walking out of the space is
   * answered server-side at read time, so this is the user's own choice and
   * nothing else. Idempotent, which matters: switching back to Nearby must not
   * fail because of state the client cannot see.
   */
  leaveSameSpace: () => {
    if (USE_MOCK) return Promise.resolve({ ok: true });
    return request<{ ok: boolean }>("/presence/same-space", {
      method: "DELETE",
    });
  },

  /** POST /presence/join — become visible/cueable in your current space. */
  join: () =>
    USE_MOCK
      ? Promise.resolve({ ok: true })
      : request<{ ok: boolean }>("/presence/join", { method: "POST" }),

  /** POST /presence/visibility — toggle whether others can see/cue you. */
  setVisibility: (visible: boolean) =>
    USE_MOCK
      ? Promise.resolve({ ok: true })
      : request<{ ok: boolean }>("/presence/visibility", {
          method: "POST",
          body: { visible },
        }),

};

export const cuesApi = {
  /**
   * POST /cue/send
   *
   * `groupId` names the SURFACE the tap came from — one Small Group's roster —
   * and nothing more. The server re-reads both memberships and the recipient's
   * consent before it accepts anything, so it is a statement about where the
   * user is, never a claim of permission.
   *
   * There is deliberately no `origin` parameter. `invite` skips the send
   * allowance, so it is minted server-side at redemption and is not an origin
   * any client can ask for.
   */
  send: (toUserId: string, opts: { groupId?: string } = {}) =>
    USE_MOCK
      ? mockApi.sendCue(toUserId)
      : request<Cue>("/cue/send", {
          method: "POST",
          body: opts.groupId
            ? { toUserId, groupId: opts.groupId }
            : { toUserId },
        }),

  /** GET /cue/inbox — pending reveal/cue requests. */
  inbox: () => (USE_MOCK ? mockApi.getInbox() : request<Cue[]>("/cue/inbox")),

  /** GET /cue/sent — pending cues I have sent. */
  sent: () =>
    USE_MOCK ? mockApi.getSentCues() : request<Cue[]>("/cue/sent"),

  /**
   * GET /cue/counts — the sender's two allowances, plus the inbox badge.
   *
   * Both allowances are rolling 24-hour windows and both come from the same
   * server-side call the send guard uses, so what is shown here and what the
   * server will accept cannot disagree.
   */
  counts: () =>
    USE_MOCK ? mockApi.getCueCounts() : request<CueCounts>("/cue/counts"),

  /**
   * POST /cue/:id/cancel — withdraw a cue that has not been answered.
   *
   * Returns the connection slot straight away. It does **not** return the
   * recipient: they stay counted against the 24-hour outreach allowance, and a
   * short pair cooldown stops the freed slot going straight back to them.
   */
  cancel: (cueId: string) =>
    USE_MOCK
      ? mockApi.cancelCue(cueId)
      : request<{ status: "cancelled" }>(
          `/cue/${encodeURIComponent(cueId)}/cancel`,
          { method: "POST" },
        ),

  /** POST /cue/:id/respond */
  respond: (cueId: string, status: "accepted" | "ignored" | "blocked") =>
    USE_MOCK
      ? mockApi.respondToCue(cueId, status)
      : request<ChatSession | null>(`/cue/${cueId}/respond`, {
          method: "POST",
          body: { status },
        }),
};

export const chatApi = {
  /**
   * GET /chat/sessions — both inbox sections, each bounded.
   *
   * The two tabs are paged independently. A single cursor over the merged list
   * would leave Inactive looking empty until enough Active rows had been paged
   * past, because the list is ordered by recency and dormant conversations are
   * by definition the least recent.
   *
   * `totalCount` is the section's real size, not `sessions.length` — the tab
   * badges and their accessibility labels report a total, and they have to stay
   * true however little of the section has loaded.
   */
  list: (options: { limit?: number; liveCursor?: string; inactiveCursor?: string } = {}) => {
    if (USE_MOCK) return mockApi.getChats();
    const params = new URLSearchParams();
    if (options.limit) params.set("limit", String(options.limit));
    if (options.liveCursor) params.set("liveCursor", options.liveCursor);
    if (options.inactiveCursor) params.set("inactiveCursor", options.inactiveCursor);
    const query = params.toString();
    return request<ChatInbox>(`/chat/sessions${query ? `?${query}` : ""}`);
  },

  /**
   * GET /chat/sessions?section=open — the contacts who are open to chat.
   *
   * The SAME query as the inbox's live section with one predicate added, which
   * is why it is a mode of this endpoint and not a second one. Open is a strict
   * subset of Active by construction: if a rule changes what counts as an
   * active conversation, Open inherits it and cannot drift.
   *
   * Paged like the others, and for the same reason — there is no ceiling on how
   * many conversations somebody has, so there can be none here.
   *
   * `myOpenUntil` is deliberately NOT read from here. It comes back with the
   * session bootstrap on `/users/me`, so the toggle is right the moment the tab
   * paints rather than after a round trip.
   */
  openSection: (options: { limit?: number; cursor?: string } = {}) => {
    if (USE_MOCK) return mockApi.getOpenChats();
    const params = new URLSearchParams({ section: "open" });
    if (options.limit) params.set("limit", String(options.limit));
    if (options.cursor) params.set("openCursor", options.cursor);
    return request<ChatSessionPage>(`/chat/sessions?${params.toString()}`);
  },

  /**
   * PUT /chat/open — turn my own signal on for a while, or off now.
   *
   * `null` is off, and it is the same call rather than a DELETE: the button is
   * a toggle, and one endpoint means the two directions cannot get out of step.
   *
   * Returns the lease the SERVER decided, never the duration asked for. The
   * client renders the remaining time from this, so a device with a skewed
   * clock or a request that took a while still shows what everyone else sees.
   */
  setOpen: (durationMinutes: number | null) =>
    USE_MOCK
      ? mockApi.setOpenToChat(durationMinutes)
      : request<{ openUntil: string | null }>("/chat/open", {
          method: "PUT",
          body: { durationMinutes },
        }),

  /**
   * GET /chat/:id — one conversation, without going through the inbox.
   *
   * The inbox pages, so the list a screen was handed may simply not contain
   * the conversation being opened — from a notification, a deep link, or a
   * thread further down than the first page. Reading it by id is the only way
   * for the chat screen to be sure of who it is talking to.
   */
  get: (chatId: string) =>
    USE_MOCK
      ? mockApi.getChat(chatId)
      : request<ChatSession>(`/chat/${chatId}`),

  /**
   * GET /chat/:id/messages — always a bounded page.
   *
   * `after` reads forward to close a gap; `before` reads backward through
   * history; neither returns the newest page, which is what opening a
   * conversation wants.
   */
  messages: (
    chatId: string,
    options: { after?: string; before?: string; limit?: number } = {},
  ) => {
    if (USE_MOCK)
      return mockApi.getMessages(chatId, options.after, options.before);
    const params = new URLSearchParams();
    if (options.after) params.set("after", options.after);
    if (options.before) params.set("before", options.before);
    if (options.limit) params.set("limit", String(options.limit));
    const query = params.toString();
    return request<ChatMessage[]>(
      `/chat/${chatId}/messages${query ? `?${query}` : ""}`,
    );
  },

  /**
   * POST /chat/:id/messages (REST fallback; live chat uses Socket.IO).
   *
   * `clientMessageId` makes the send safe to repeat: the server stores the
   * first one and returns that same message for any later attempt carrying the
   * key, so a retry after a lost response cannot post twice. Keep it stable
   * across retries of one send and different between sends — that is the whole
   * contract.
   */
  send: (
    chatId: string,
    content: string,
    replyToMessageId?: string,
    clientMessageId?: string,
  ) =>
    USE_MOCK
      ? mockApi.sendMessage(chatId, content, replyToMessageId)
      : request<ChatMessage>(`/chat/${chatId}/messages`, {
          method: "POST",
          body: { content, replyToMessageId, clientMessageId },
        }),

  /** POST /chat/:id/messages/:messageId/reaction */
  react: (chatId: string, messageId: string, reaction: string | null) =>
    USE_MOCK
      ? mockApi.reactMessage(chatId, messageId, reaction)
      : request<ChatMessage>(`/chat/${chatId}/messages/${messageId}/reaction`, {
          method: "POST",
          body: { reaction },
        }),

  /** POST /chat/:id/end — exit quietly (→ Inactive), or block. */
  end: (chatId: string, outcome: EndConversationOutcome = "quiet") =>
    USE_MOCK
      ? mockApi.endChat(chatId, outcome)
      : request<{ ok: boolean }>(`/chat/${chatId}/end`, {
          method: "POST",
          body: { outcome },
        }),

  /**
   * POST /chat/:id/clear — wipe my copy of the thread.
   *
   * No body: clearing is always everything up to now, and always only for the
   * caller. The conversation itself is untouched and the other person is never
   * told, which is why this is not an ending.
   */
  clear: (chatId: string) =>
    USE_MOCK
      ? mockApi.clearMessages(chatId)
      : request<ChatSession>(`/chat/${chatId}/clear`, { method: "POST" }),

  /**
   * POST /chat/:id/read — the thread is on my screen; advance my read mark.
   * The 1:1 twin of `groupApi.markRead`: no body, idempotent, fire-and-forget.
   */
  markRead: (chatId: string) =>
    USE_MOCK
      ? mockApi.markChatRead(chatId)
      : request<{ ok: boolean }>(`/chat/${chatId}/read`, { method: "POST" }),

  /**
   * POST /chat/:id/reopen — ask to bring an Inactive conversation back.
   *
   * No body, and no parameter to add one to. A reopen request cannot carry a
   * message: being able to attach one would make this a way to reach someone
   * who has already stopped replying.
   */
  /**
   * POST /chat/:id/unblock — lift a block from the conversation it sits on.
   *
   * The counterpart of `requestReopen`, and shaped the same way. Unblocking does
   * not reopen anything, and it does not put the pair back in each other's
   * nearby list: the thread becomes an ordinary dormant one, and the way back is
   * `requestReopen` followed by the other person accepting. Only the person who
   * lifted the block is offered that — the server decides, and sends it back as
   * `canRequestReopen`.
   */
  unblock: (chatId: string) =>
    USE_MOCK
      ? mockApi.unblockChat(chatId)
      : request<ChatSession>(`/chat/${chatId}/unblock`, { method: "POST" }),

  requestReopen: (chatId: string) =>
    USE_MOCK
      ? mockApi.requestReopen(chatId)
      : request<ChatSession>(`/chat/${chatId}/reopen`, { method: "POST" }),

  /**
   * POST /chat/:id/resume — walk back into a conversation you quietly left.
   *
   * A separate call from `requestReopen`, not a flag on it, because that one
   * notifies the other person and this one must never be able to. Nothing is
   * sent, nothing is written to the thread, and their side is untouched.
   */
  resume: (chatId: string) =>
    USE_MOCK
      ? mockApi.resumeChat(chatId)
      : request<ChatSession>(`/chat/${chatId}/resume`, { method: "POST" }),

  /** POST /chat/:id/reopen/respond — "Reopen conversation" / "Not now". */
  respondToReopen: (chatId: string, accept: boolean) =>
    USE_MOCK
      ? mockApi.respondToReopen(chatId, accept)
      : request<ChatSession>(`/chat/${chatId}/reopen/respond`, {
          method: "POST",
          body: { accept },
        }),

  /** GET /chat/:id/photo-exchange — state for the card. */
  photoExchange: (chatId: string) =>
    USE_MOCK
      ? mockApi.getPhotoExchange(chatId)
      : request<PhotoExchangeState>(`/chat/${chatId}/photo-exchange`),

  /**
   * POST /chat/:id/photo-exchange/request — ask, with your photo attached.
   *
   * The photo goes with the ask. There is no state where you have requested
   * but not yet chosen, and no text field to carry a message alongside it.
   */
  requestPhotoExchange: (chatId: string, photo: PhotoUpload) =>
    USE_MOCK
      ? mockApi.requestPhotoExchange(chatId)
      : uploadImage<PhotoExchangeState>(
          `/chat/${chatId}/photo-exchange/request`,
          photo,
        ),

  /** POST /chat/:id/photo-exchange/accept — agree, with your photo. Reveals both. */
  acceptPhotoExchange: (chatId: string, photo: PhotoUpload) =>
    USE_MOCK
      ? mockApi.acceptPhotoExchange(chatId)
      : uploadImage<PhotoExchangeState>(
          `/chat/${chatId}/photo-exchange/accept`,
          photo,
        ),

  /** POST /chat/:id/photo-exchange/decline — "Not now". */
  declinePhotoExchange: (chatId: string) =>
    USE_MOCK
      ? mockApi.declinePhotoExchange(chatId)
      : request<PhotoExchangeState>(`/chat/${chatId}/photo-exchange/decline`, {
          method: "POST",
        }),

  /** POST /chat/:id/photo-exchange/cancel — withdraw your own request. */
  cancelPhotoExchange: (chatId: string) =>
    USE_MOCK
      ? mockApi.cancelPhotoExchange(chatId)
      : request<PhotoExchangeState>(`/chat/${chatId}/photo-exchange/cancel`, {
          method: "POST",
        }),

  /**
   * Where a photo's bytes live, plus the header that authorises them.
   *
   * Not a shareable link: the route re-checks the viewer on every request, so
   * this stops working the instant the photo is deleted or reported.
   */
  photoSource: (chatId: string, assetId: string, size: "thumb" | "full") =>
    authedImageSource(`/chat/${chatId}/photo/${assetId}?size=${size}`),

  /** DELETE /chat/:id/photo/:assetId — take your own photo back, for both of you. */
  deletePhoto: (chatId: string, assetId: string) =>
    USE_MOCK
      ? mockApi.deletePhoto(chatId, assetId)
      : request<{ ok: boolean }>(`/chat/${chatId}/photo/${assetId}`, {
          method: "DELETE",
        }),

  /**
   * GET /moderation/blocked — everyone you have blocked, newest first.
   *
   * The complete list, which the per-conversation Unblock cannot be: blocking is
   * reachable from declining a Cue, and no conversation exists in that case. The
   * thread is the natural place to unblock when there IS one; this is what makes
   * sure every block has somewhere to be lifted from.
   */
  blocked: () =>
    USE_MOCK
      ? mockApi.getBlockedUsers()
      : request<BlockedUser[]>("/moderation/blocked"),

  /** POST /moderation/unblock — lift a block by person, with or without a thread. */
  unblockUser: (userId: string) =>
    USE_MOCK
      ? mockApi.unblockUser(userId)
      : request<{ ok: boolean }>("/moderation/unblock", {
          method: "POST",
          body: { userId },
        }),

  /** POST /moderation/report — file a report + apply protective actions. */
  report: (input: ReportInput) =>
    USE_MOCK
      ? mockApi.reportUser(input)
      : request<{ ok: boolean }>("/moderation/report", {
          method: "POST",
          body: input,
        }),

};

const MOCK_DECK_HELPER_BASES = [
  {
    id: "START",
    category: "start",
    main: "What’s something good you’ve discovered lately?",
    possibleFollowUp: "What made it stand out to you?",
  },
  {
    id: "KEEP",
    category: "keep_going",
    main: "What’s a topic you could talk about for ages without getting bored?",
    possibleFollowUp:
      "What usually gets people interested once you start explaining it?",
  },
  {
    id: "FUN",
    category: "fun",
    main: "You can have one completely useless superpower. What are you choosing?",
    possibleFollowUp: "How would you still find a way to show it off?",
  },
  {
    id: "KNOW",
    category: "get_to_know",
    main:
      "When you need to recharge, do you prefer being alone or having quiet company?",
    possibleFollowUp: "What usually makes that time feel properly restful?",
  },
  {
    id: "DEEP",
    category: "go_deeper",
    main: "What’s a perspective you’ve changed in a way you’re glad about?",
    possibleFollowUp: "What helped the new view make sense to you?",
  },
  {
    id: "PLAN",
    category: "make_a_plan",
    main: "Would you be up for grabbing a coffee or tea sometime next week?",
    possibleFollowUp: "Is there a day that tends to work better for you?",
  },
  {
    id: "WRAP",
    category: "wrap_up",
    main: "I’m going to head off, but I’ve really enjoyed chatting.",
    possibleFollowUp: "Same here. Have a good rest of your day.",
  },
] satisfies ConversationDeckBootstrapResponse["helpers"];

const MOCK_DECK_HELPERS =
  MOCK_DECK_HELPER_BASES.flatMap((helper) =>
    Array.from({ length: 20 }, (_, index) => ({
      ...helper,
      id: `${helper.id}-MOCK-${String(index + 1).padStart(3, "0")}`,
    })),
  );

export const conversationDeckApi = {
  bootstrap: (installationId: string, idempotencyKey: string) =>
    USE_MOCK
      ? Promise.resolve<ConversationDeckBootstrapResponse>({
          bankVersion: 17,
          schemaVersion: 1,
          revision: 1,
          helpers: MOCK_DECK_HELPERS,
          invalidated: false,
        })
      : request<ConversationDeckBootstrapResponse>(
          "/conversation-deck/bootstrap",
          {
            method: "POST",
            body: { installationId, idempotencyKey },
          },
        ),

  refill: (
    installationId: string,
    idempotencyKey: string,
    category: ConversationDeckServerCategory,
    unusedCount: number,
  ) =>
    USE_MOCK
      ? (() => {
          const revision = Date.now();
          return Promise.resolve<ConversationDeckRefillResponse>({
            bankVersion: 17,
            schemaVersion: 1,
            revision,
            category,
            helpers: MOCK_DECK_HELPERS.filter(
              (helper) => helper.category === category,
            )
              .slice(0, Math.max(1, 20 - unusedCount))
              .map((helper) => ({
                ...helper,
                id: `${helper.id}-R${revision}`,
              })),
            invalidated: false,
          });
        })()
      : request<ConversationDeckRefillResponse>("/conversation-deck/refill", {
          method: "POST",
          body: { installationId, idempotencyKey, category, unusedCount },
        }),
};

export const groupApi = {
  /**
   * GET /groups — groups the viewer can join, narrowed by what they asked for.
   *
   * Filters are sent, never applied to the answer. The server caps the list, so
   * filtering here would fill that ceiling with groups the viewer did not ask
   * for and hide ones they did — the same reason the nearby list stopped
   * filtering on this side.
   */
  list: (filters: GroupBrowseFilters = {}) => {
    if (USE_MOCK) return mockApi.getGroups();
    const query = new URLSearchParams();
    if (filters.vibe) query.set("vibe", filters.vibe);
    if (filters.maxMembers) query.set("maxMembers", String(filters.maxMembers));
    if (filters.ageBandMinFloor !== undefined) {
      query.set("ageBandMinFloor", String(filters.ageBandMinFloor));
    }
    if (filters.ageBandMaxFloor !== undefined) {
      query.set("ageBandMaxFloor", String(filters.ageBandMaxFloor));
    }
    const qs = query.toString();
    return request<SmallGroup[]>(`/groups${qs ? `?${qs}` : ""}`);
  },

  /**
   * GET /groups/inbox — both sections. Active is whole (a finite plan limit
   * bounds concurrent membership); inactive history pages by cursor, exactly
   * as the one-to-one inbox's dormant section does.
   */
  inbox: (options: { inactiveCursor?: string } = {}) => {
    if (USE_MOCK) return mockApi.getGroupInbox();
    const params = new URLSearchParams();
    if (options.inactiveCursor) params.set("inactiveCursor", options.inactiveCursor);
    const query = params.toString();
    return request<SmallGroupInboxSections>(`/groups/inbox${query ? `?${query}` : ""}`);
  },

  /** POST /groups/:id/read — mark this group's messages as read. */
  markRead: (groupId: string) =>
    USE_MOCK
      ? Promise.resolve({ ok: true })
      : request<{ ok: boolean }>(`/groups/${groupId}/read`, { method: "POST" }),

  create: (body: CreateSmallGroupInput) =>
    USE_MOCK
      ? mockApi.createGroup(body)
      : request<SmallGroup>("/groups", { method: "POST", body }),

  get: (groupId: string) =>
    USE_MOCK
      ? mockApi.getGroup(groupId)
      : request<SmallGroup>(`/groups/${groupId}`),

  /**
   * GET /groups/:id/invite — what an invite link resolves to.
   *
   * Distinct from `get()` because that one refuses anyone outside the group's
   * range outright, which is right for browsing and wrong for an invitation:
   * the link must always show the group, with the range explained at the join.
   */
  invite: (groupId: string) =>
    USE_MOCK
      ? mockApi.getGroupInvite(groupId)
      : request<SmallGroupInvite>(`/groups/${groupId}/invite`),

  /** PATCH /groups/:id/radius — host only; takes effect immediately. */
  setRadius: (groupId: string, radiusM: number) =>
    USE_MOCK
      ? mockApi.setGroupRadius(groupId, radiusM)
      : request<SmallGroup>(`/groups/${groupId}/radius`, {
          method: "PATCH",
          body: { radiusM },
        }),

  /**
   * POST /groups/:id/join
   *
   * `allowMemberCues` is the arrival intent the join sheet collected: Cue me,
   * or Join only. It rides on the join rather than following it, because a
   * separate second call would leave a window in which somebody who chose Cue
   * Me is in the group with the permission off — and would make the OFF state
   * depend on a request having been made rather than on a choice.
   */
  join: (groupId: string, opts: { allowMemberCues?: boolean } = {}) =>
    USE_MOCK
      ? mockApi.joinGroup(groupId)
      : request<SmallGroup>(`/groups/${groupId}/join`, {
          method: "POST",
          body: { allowMemberCues: opts.allowMemberCues === true },
        }),

  /**
   * PATCH /groups/:id/cue-consent — change your mind, from the roster.
   *
   * Turning it off revokes nothing already sent: a pending Cue is a question
   * that has been asked, and the recipient still has Accept, Ignore and Block.
   */
  setCueConsent: (groupId: string, allowMemberCues: boolean) =>
    USE_MOCK
      ? mockApi.getGroup(groupId)
      : request<SmallGroup>(`/groups/${groupId}/cue-consent`, {
          method: "PATCH",
          body: { allowMemberCues },
        }),

  leave: (groupId: string) =>
    USE_MOCK
      ? mockApi.leaveGroup(groupId)
      : request<{ ok: boolean }>(`/groups/${groupId}/leave`, {
          method: "POST",
        }),

  /** DELETE /groups/:id — remove an ENDED group from MY inbox. Per-member:
   *  nobody else's history moves. The server refuses active groups (Leave is
   *  that path). */
  delete: (groupId: string) =>
    USE_MOCK
      ? mockApi.deleteGroup(groupId)
      : request<{ ok: boolean }>(`/groups/${groupId}`, { method: "DELETE" }),

  /**
   * A bounded page, always.
   *
   * `after` is what arrived since a message — a catch-up, so callers keep asking
   * until one comes back short. `before` is the page preceding one, for reaching
   * back through history. Neither is the NEWEST page, which is what opening the
   * thread wants and what this used to return the whole thread instead of.
   */
  messages: (
    groupId: string,
    options: { after?: string; before?: string; limit?: number } = {},
  ) => {
    if (USE_MOCK)
      return mockApi.getGroupMessages(
        groupId,
        options.after,
        options.before,
        options.limit,
      );
    const params = new URLSearchParams();
    if (options.after) params.set("after", options.after);
    if (options.before) params.set("before", options.before);
    if (options.limit) params.set("limit", String(options.limit));
    const query = params.toString();
    return request<SmallGroupMessage[]>(
      `/groups/${groupId}/messages${query ? `?${query}` : ""}`,
    );
  },

  /** POST /groups/:id/messages. `clientMessageId` as in `chatApi.send`. */
  send: (groupId: string, content: string, clientMessageId?: string) =>
    USE_MOCK
      ? mockApi.sendGroupMessage(groupId, content)
      : request<SmallGroupMessage>(`/groups/${groupId}/messages`, {
          method: "POST",
          body: { content, clientMessageId },
        }),

  suggestMeetup: (groupId: string, spot: string, customSpot?: string) =>
    USE_MOCK
      ? mockApi.suggestGroupMeetup(groupId, spot, customSpot)
      : request<SmallGroupMessage>(`/groups/${groupId}/suggest-meetup`, {
          method: "POST",
          body: { spot, customSpot },
        }),

  removeMember: (
    groupId: string,
    memberId: string,
    reason:
      | "host_removed"
      | "spam"
      | "uncomfortable"
      | "unsafe"
      | "other" = "host_removed",
  ) =>
    USE_MOCK
      ? mockApi.removeGroupMember(groupId, memberId)
      : request<{
          status: "removed";
          groupId: string;
          memberId: string;
          removedAt: string;
        }>(`/groups/${groupId}/members/${memberId}/remove`, {
          method: "POST",
          body: { reason },
        }),

  ready: (groupId: string) =>
    USE_MOCK
      ? mockApi.readyGroup(groupId)
      : request<SmallGroupMessage[]>(`/groups/${groupId}/ready`, {
          method: "POST",
        }),

  typing: (groupId: string, isTyping: boolean) =>
    USE_MOCK
      ? Promise.resolve({ ok: true })
      : request<{ ok: boolean }>(`/groups/${groupId}/typing`, {
          method: "POST",
          body: { isTyping },
        }),
};

export const revealApi = {
  /** POST /reveal/:chatId/request — both users must agree. */
  request: (chatId: string, fields: string[] = []) =>
    USE_MOCK
      ? mockApi.requestReveal(chatId)
      : request<{ status: "pending_peer" | "revealed"; result?: RevealResult }>(
          `/reveal/${chatId}/request`,
          { method: "POST", body: { fields } },
        ),
};

/**
 * Conversation Practice — the private AI roleplay coach. Completely separate
 * from real chats: distinct endpoints, sessions, and messages.
 */
export const practiceApi = {
  /** GET /practice/scenarios — active practice scenarios. */
  scenarios: () =>
    USE_MOCK
      ? mockApi.practiceScenarios()
      : request<PracticeScenario[]>("/practice/scenarios", { method: "GET" }),

  /** GET /practice/credits — remaining replies + billing period + warning. */
  credits: () =>
    USE_MOCK
      ? mockApi.practiceCredits()
      : request<PracticeCredits>("/practice/credits", { method: "GET" }),

  /** GET /practice/packs — purchasable reply packs. */
  packs: () =>
    USE_MOCK
      ? mockApi.practicePacks()
      : request<PracticePack[]>("/practice/packs", { method: "GET" }),

  /**
   * GET /practice/messages/:id/audio — Voice Practice (Phase B). Fetches the
   * synthesized coach-voice audio as an auth'd Blob. No mock path: TTS must be
   * enabled server-side, and callers guard on the dev flag. Throws on non-2xx
   * (e.g. 503 when TTS is disabled) so the UI can fail closed to text.
   *
   * Goes through `fetchAuthedBlob` for the session refresh: this used to be a
   * bare `fetch` carrying a token read at call time, so once a practice session
   * outlived the access token every clip 401'd and the coach went silent —
   * indistinguishable from TTS being switched off.
   */
  messageAudio: (messageId: string, voice?: PracticeVoice): Promise<Blob> =>
    fetchAuthedBlob(
      `${API_BASE_URL}/practice/messages/${encodeURIComponent(messageId)}/audio${
        voice ? `?voice=${encodeURIComponent(voice)}` : ""
      }`,
      "voice_audio",
    ),

  /**
   * GET /practice/sessions/:id/intake/:index/audio — Voice Practice (Phase B).
   * Same as `messageAudio` but for a BYO intake guide turn (which isn't stored
   * as a message). Auth'd Blob; throws on non-2xx so the UI fails closed to text.
   */
  intakeAudio: (
    sessionId: string,
    index: number,
    voice?: PracticeVoice,
  ): Promise<Blob> =>
    fetchAuthedBlob(
      `${API_BASE_URL}/practice/sessions/${encodeURIComponent(sessionId)}/intake/${index}/audio${
        voice ? `?voice=${encodeURIComponent(voice)}` : ""
      }`,
      "voice_audio",
    ),

  /** GET /practice/active-sessions — all in-progress scenario sessions. */
  activeSessions: () =>
    USE_MOCK
      ? mockApi.practiceActiveSessions()
      : request<PracticeSessionView["session"][]>(
          "/practice/active-sessions",
          { method: "GET" },
        ),

  /** POST /practice/sessions — start a session for a scenario.
   *  sceneSource "byo" starts a bring-your-own session (no curated pack; the
   *  user recounts their own situation in a short intake). */
  startSession: (scenarioId: string, sceneSource: "curated" | "byo" = "curated") =>
    USE_MOCK
      ? mockApi.practiceStartSession(scenarioId, sceneSource)
      : request<PracticeSessionView>("/practice/sessions", {
          method: "POST",
          body: { scenarioId, sceneSource },
        }),

  /** POST /practice/sessions/:id/use-own-scene — switch a fresh curated session
   *  to bring-your-own (drops the pack, enters intake). Only before any turn. */
  useOwnScene: (id: string) =>
    USE_MOCK
      ? mockApi.practiceUseOwnScene(id)
      : request<PracticeSession>(
          `/practice/sessions/${encodeURIComponent(id)}/use-own-scene`,
          { method: "POST" },
        ),

  /** POST /practice/sessions/:id/intake — one turn of the BYO setup conversation.
   *  Free (no credit): the guide understands the user's own situation, applies
   *  the safety gate, and captures a scene when ready. */
  intakeTurn: (id: string, content: string) =>
    USE_MOCK
      ? mockApi.practiceIntakeTurn(id, content)
      : request<ByoIntakeResult>(
          `/practice/sessions/${encodeURIComponent(id)}/intake`,
          { method: "POST", body: { content } },
        ),

  /**
   * GET /practice/sessions/:id — a session and the NEWEST page of its
   * transcript.
   *
   * Not the whole transcript. Every message is encrypted at rest and decrypted
   * individually, so an unbounded read here was unbounded CPU on the API for a
   * screen that opens at the turn you were on. `olderMessages` reaches the rest.
   */
  getSession: (id: string) =>
    USE_MOCK
      ? mockApi.practiceGetSession(id)
      : request<PracticeSessionView>(
          `/practice/sessions/${encodeURIComponent(id)}`,
          { method: "GET" },
        ),

  /** GET /practice/sessions/:id/messages?before= — the page before a message. */
  olderMessages: (
    id: string,
    options: { before: string; limit?: number },
  ) => {
    if (USE_MOCK) return Promise.resolve({ messages: [] as PracticeMessage[] });
    const params = new URLSearchParams({ before: options.before });
    if (options.limit) params.set("limit", String(options.limit));
    return request<{ messages: PracticeMessage[] }>(
      `/practice/sessions/${encodeURIComponent(id)}/messages?${params.toString()}`,
      { method: "GET" },
    );
  },

  /** POST /practice/sessions/:id/messages — send a turn, get the coach reply. */
  sendMessage: (
    id: string,
    content: string,
    idempotencyKey?: string,
    signal?: AbortSignal,
    // Voice Practice (§11): approximate barge-in metadata for this turn.
    interruption?: { trigger: "voice" | "tap"; elapsedMs: number; totalDurationMs?: number },
  ) =>
    USE_MOCK
      ? mockApi.practiceSendMessage(id, content)
      : request<PracticeSendResult>(
          `/practice/sessions/${encodeURIComponent(id)}/messages`,
          { method: "POST", body: { content, idempotencyKey, interruption }, signal },
        ),

  /** POST /practice/sessions/:id/try-another — swap to a different stored pack. */
  tryAnotherSetting: (id: string) =>
    USE_MOCK
      ? mockApi
          .practiceGetSession(id)
          .then((v) => ({ session: v.session, pack: v.pack }))
      : request<{ session: PracticeSession; pack: PracticePackView | null }>(
          `/practice/sessions/${encodeURIComponent(id)}/try-another`,
          { method: "POST" },
        ),

  /** POST /practice/sessions/:id/resume — reopen an ended session ("Continue"). */
  resume: (id: string) =>
    USE_MOCK
      ? mockApi
          .practiceGetSession(id)
          .then((v) => ({ ...v.session, status: "active" as const }))
      : request<PracticeSession>(
          `/practice/sessions/${encodeURIComponent(id)}/resume`,
          { method: "POST" },
        ),

  /** POST /practice/sessions/:id/mode — change guidance level (guided/roleplay/challenge). */
  setMode: (id: string, mode: PracticeMode) =>
    USE_MOCK
      ? mockApi.practiceGetSession(id).then((v) => v.session)
      : request<PracticeSession>(
          `/practice/sessions/${encodeURIComponent(id)}/mode`,
          { method: "POST", body: { mode } },
        ),

  /** POST /practice/turns/:requestId/cancel — durable server-side Stop. */
  cancelTurn: (requestId: string) =>
    USE_MOCK
      ? Promise.resolve({ status: "cancelled", creditDeducted: false })
      : request<{ status: string; creditDeducted: boolean }>(
          `/practice/turns/${encodeURIComponent(requestId)}/cancel`,
          { method: "POST" },
        ),

  /** POST /practice/sessions/:id/summary — summarize + end (no credit needed). */
  summarize: (id: string) =>
    USE_MOCK
      ? mockApi.practiceSummarize(id)
      : request<PracticeSummaryResponse>(
          `/practice/sessions/${encodeURIComponent(id)}/summary`,
          { method: "POST" },
        ),

  /** POST /practice/sessions/:id/end — end without a summary. */
  end: (id: string) =>
    USE_MOCK
      ? mockApi.practiceEnd(id)
      : request<PracticeSessionView["session"]>(
          `/practice/sessions/${encodeURIComponent(id)}/end`,
          { method: "POST" },
        ),

  /** DELETE /practice/sessions/:id — clear/delete a session. */
  clearSession: (id: string) =>
    USE_MOCK
      ? mockApi.practiceClearSession(id)
      : request<{ ok: boolean }>(
          `/practice/sessions/${encodeURIComponent(id)}`,
          { method: "DELETE" },
        ),

  /** POST /practice/credits/purchase — start checkout for a reply pack.
   *  Currency is the server's call (from the caller's location), not a body field. */
  purchase: (pack: string) =>
    USE_MOCK
      ? mockApi.practicePurchase(pack)
      : request<{ clientSecret?: string; url?: string }>(
          "/practice/credits/purchase",
          {
            method: "POST",
            body: { pack },
          },
        ),
};

export { ApiError, SessionExpiredError, TimeoutError } from "./client";
